import { Archive, Check, Clock, FileText, FolderOpen, Plus, Settings } from "lucide-react";
import {
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "components/common/dropdown/DropDownV2";
import { WORKSPACE_PRODUCT_NAV } from "pages/settings/workspace/WorkspaceSettings.sections";
import { cn } from "utils/cn";
import { sortProjectsForRecentMenu } from "../../workspaceDropdown.utils";
import { DropdownSectionLabel } from "../DropdownSectionLabel";
import { WorkspaceProfileInfo } from "../WorkspaceProfileInfo";
import { WorkspaceSwitcherList } from "../WorkspaceSwitcherList";
import { WorkspaceProductShellMenuProps } from "./WorkspaceProductShellMenu.types";

export const WorkspaceProductShellMenu = ({
  activeWorkspaceId,
  currentWorkspaceName,
  currentWorkspaceRole,
  currentWorkspaceImagePath,
  currentProjectId,
  activeWorkspace,
  workspaces,
  canCreateProject,
  canAccessWorkspaceSettings,
  onAllProjects,
  onCreateProject,
  onWorkspaceSettings,
  onOpenProject,
  onSwitchWorkspace,
  onClose,
}: WorkspaceProductShellMenuProps) => {
  const showWorkspaceSwitcher = workspaces.length > 1;
  const allProjects = activeWorkspace?.projects ?? [];
  const { recentProjects, totalCount } = sortProjectsForRecentMenu(allProjects, currentProjectId);
  const showRecents = recentProjects.length > 0 && Boolean(activeWorkspaceId);
  const hasNoProjects = totalCount === 0;

  const handleWorkspaceSettings = () => {
    onWorkspaceSettings();
    onClose();
  };

  return (
    <>
      {activeWorkspaceId ? (
        <>
          <DropdownMenuLabel className="p-0 font-normal">
            <WorkspaceProfileInfo
              workspaceId={activeWorkspaceId}
              workspaceName={currentWorkspaceName}
              role={currentWorkspaceRole}
              imagePath={currentWorkspaceImagePath}
            />
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
        </>
      ) : null}

      <DropdownSectionLabel>Projects</DropdownSectionLabel>

      {showRecents ? (
        <DropdownMenuGroup className="max-h-48 overflow-y-auto">
          {recentProjects.map((project) => {
            const isCurrent = project.id === currentProjectId;

            return (
              <DropdownMenuItem
                key={project.id}
                aria-current={isCurrent ? "true" : undefined}
                onClick={() => {
                  onOpenProject(activeWorkspaceId as string, project.id);
                  onClose();
                }}
                className={cn(isCurrent && "bg-muted")}
              >
                <FileText
                  className={cn(
                    "h-4 w-4 shrink-0",
                    isCurrent ? "text-foreground" : "text-muted-foreground"
                  )}
                />
                <span className="min-w-0 flex-1 truncate">{project.name}</span>
                {project.trialExpiryDate ? (
                  <Clock className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
                ) : null}
                {project.archived ? (
                  <Archive className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" aria-hidden />
                ) : null}
                {isCurrent ? <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden /> : null}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuGroup>
      ) : null}

      <DropdownMenuGroup>
        {hasNoProjects && canCreateProject ? (
          <DropdownMenuItem
            onClick={() => {
              onCreateProject();
              onClose();
            }}
            className="bg-muted/60"
          >
            <Plus className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="flex-1 font-medium">Create your first project</span>
          </DropdownMenuItem>
        ) : null}

        <DropdownMenuItem
          onClick={() => {
            onAllProjects();
            onClose();
          }}
        >
          <FolderOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="flex-1">{WORKSPACE_PRODUCT_NAV.title}</span>
          {totalCount > 0 ? (
            <span className="shrink-0 text-xs text-muted-foreground/70">{totalCount}</span>
          ) : null}
        </DropdownMenuItem>

        {canCreateProject && !hasNoProjects ? (
          <DropdownMenuItem
            onClick={() => {
              onCreateProject();
              onClose();
            }}
          >
            <Plus className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="flex-1">New project</span>
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuGroup>

      {showWorkspaceSwitcher ? (
        <>
          <DropdownMenuSeparator />
          <DropdownSectionLabel>Switch workspace</DropdownSectionLabel>
          <WorkspaceSwitcherList
            workspaces={workspaces}
            activeWorkspaceId={activeWorkspaceId}
            onSwitchWorkspace={onSwitchWorkspace}
            onClose={onClose}
          />
        </>
      ) : null}

      {canAccessWorkspaceSettings ? (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem onClick={handleWorkspaceSettings}>
              <Settings className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="flex-1">Workspace settings</span>
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </>
      ) : null}
    </>
  );
};
